import ControlFilter from "components/shared/ControlFilter";
import { Spinner } from "components/shared/Spinner";
import { SmallBodyHighlight } from "helpers/TypographyComponents";
import { useTranslation } from "react-i18next";
import type { AgreementTemplate } from "store/agreementsStore";

import AgreementRow from "../AgreementRow";

/**
 * Render Agreement templates tab in settings
 */
interface IAgreementTemplatesTab {
    /**
     * Agreement templates data
     */
    agreementTemplates: AgreementTemplate[];
    /**
     * Render page loading state
     */
    isLoadingTemplates: boolean;
    /**
     * ID of the last created template, null if none
     */
    lastAddedTemplateId: number | null;
    /**
     * Handle open agreement template drawer
     */
    handleOpenDrawer: (agreement: AgreementTemplate, isDefaultTemplate: boolean) => void;
    /**
     * Current type filter
     */
    templateTypeFilter: "agreement" | "avenant" | "framework_contract" | undefined;
    /**
     * Change type filter
     */
    onTemplateTypeFilterChange: (type: "agreement" | "avenant" | "framework_contract" | undefined) => void;
}

const AgreementTemplatesTab = ({
    agreementTemplates,
    isLoadingTemplates,
    lastAddedTemplateId,
    handleOpenDrawer,
    templateTypeFilter,
    onTemplateTypeFilterChange,
}: IAgreementTemplatesTab) => {
    const [t] = useTranslation();

    const filteredTemplates = templateTypeFilter
        ? agreementTemplates?.filter((a) => a?.type === templateTypeFilter)
        : agreementTemplates;

    const typeFilterOptions = [
        { value: undefined, label: t("ContractValidation.All") },
        { value: "agreement", label: t("ContractValidation.Agreements") },
        { value: "framework_contract", label: t("ContractValidation.FrameworkContracts") },
        { value: "avenant", label: t("ContractValidation.Avenants") },
    ];

    return (
        <>
            {isLoadingTemplates ? (
                <div className="settings-contracts-loading">
                    <Spinner />
                    <SmallBodyHighlight color="grey">{t("ContractSettings.TemplatesLoading")}</SmallBodyHighlight>
                </div>
            ) : (
                <div className="contract-editor">
                    <div className="w-fit">
                        <ControlFilter
                            filterLabel={`${t("ContractValidation.TypeDocumentLabel")} :`}
                            filterOptions={typeFilterOptions}
                            controlValue={templateTypeFilter}
                            onValueChange={(val) => onTemplateTypeFilterChange(val)}
                            selector
                            btnWidth="180px"
                        />
                    </div>
                    <div className="agreement-rows">
                        {filteredTemplates?.map((agreement) => {
                            if (!agreement) return null;
                            const originalIndex = agreementTemplates.indexOf(agreement);
                            const isLastAddedTemplate = agreement.id === lastAddedTemplateId;
                            return (
                                <AgreementRow
                                    agreement={agreement}
                                    isLastAddedTemplate={isLastAddedTemplate}
                                    index={originalIndex}
                                    key={`agreement-${agreement.id}`}
                                    handleOpenDrawer={handleOpenDrawer}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default AgreementTemplatesTab;
